import { Chip, ChipsList } from '@naf-simba/elements-chips';
import { Check } from '@naf-simba/building-blocks-icons/General';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { GridCol } from '@naf/grid';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { TextVariant } from '@naf/text';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { useFetchFavoriteBenefits } from '@hooks/useFetchFavoriteBenefits';
import { MembershipBenefitSearchFilterType } from '@appTypes/MembershipBenefitSearchFilterType';
import { actions } from '@redux/modules/membershipBenefitSearch';
import useSelector from '@redux/typedHooks';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { AnimatedFiltersContainer, CategoryButton, CategoryButtonWrapper, Chevron, FiltersWrapper } from './Styles';

interface Props {
  availableTags?: MembershipBenefitSearchFilterType[];
  tags?: MembershipBenefitSearchFilterType['slug'][];
  countTotalItemsInTextSearch: number;
  favoritesOnly?: boolean;
}

export const Filters = ({ availableTags, tags = [], countTotalItemsInTextSearch, favoritesOnly = false }: Props) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth0();
  const { simpleToken } = useAuth0Token();
  const { benefits: fetchedFavoriteBenefits, getFavoriteBenefits } = useFetchFavoriteBenefits();
  // Sourced from redux (not the hook's own local state) so a favorite added/removed from any card
  // elsewhere in the app is reflected here too, instead of only the copy fetched on mount.
  const favoriteBenefits = useSelector((state) => state.membershipBenefitSearch.favoriteBenefits);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.values.sm}px)`);
  const [showTags, setShowTags] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const hasNoSelectedTags = tags.length === 0 && !favoritesOnly;
  const isVisible = isMobile ? showTags : true;
  const favoriteBenefitsCount = favoriteBenefits?.length ?? 0;
  // The backend's count is scoped to favorites while favoritesOnly is active, so "Alle rabatter"
  // needs its own snapshot of the last non-favorites-scoped total to stay stable across the toggle.
  const lastKnownTotalRef = useRef(countTotalItemsInTextSearch);
  if (!favoritesOnly) {
    lastKnownTotalRef.current = countTotalItemsInTextSearch;
  }
  const allBenefitsCount = favoritesOnly ? lastKnownTotalRef.current : countTotalItemsInTextSearch;

  useEffect(() => {
    if (simpleToken) {
      getFavoriteBenefits();
    }
  }, [simpleToken, getFavoriteBenefits]);

  useEffect(() => {
    if (fetchedFavoriteBenefits) {
      dispatch(actions.setFavoriteBenefits({ benefits: fetchedFavoriteBenefits }));
    }
  }, [fetchedFavoriteBenefits, dispatch]);

  const sortedFilters = useMemo(
    () => [...(availableTags ?? [])].sort((a, b) => a.title.localeCompare(b.title)),
    [availableTags],
  );

  // Favorites-scoped count per tag, keyed by title — the only per-item tag data this endpoint
  // exposes (the list response's `tags` array is always empty). Only counts each benefit's
  // primary tag, so a benefit that only matches a chip via a secondary tag won't be reflected
  // here, unlike the backend's own count query which checks both.
  const favoriteCountsByTagTitle = useMemo(() => {
    const counts = new Map<string, number>();
    favoriteBenefits?.forEach((benefit) => {
      const title = benefit.primaryTag?.title;
      if (title) counts.set(title, (counts.get(title) ?? 0) + 1);
    });
    return counts;
  }, [favoriteBenefits]);

  const displayedFilters = useMemo(() => {
    if (!favoritesOnly) return sortedFilters;
    return sortedFilters
      .map((tag) => ({ ...tag, count: favoriteCountsByTagTitle.get(tag.title) ?? 0 }))
      .filter((tag) => tag.count > 0);
  }, [sortedFilters, favoritesOnly, favoriteCountsByTagTitle]);

  useEffect(() => {
    if (filtersRef.current) {
      const updateHeight = () => {
        if (filtersRef.current) {
          const contentHeight = filtersRef.current.scrollHeight;
          setHeight(contentHeight);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
    return () => {};
  }, [sortedFilters, isMobile]);

  if ((!sortedFilters || sortedFilters.length === 0) && !(isAuthenticated && favoriteBenefitsCount > 0)) {
    return null;
  }

  return (
    <GridCol s="12" m="12" l="12" xl="12">
      {isMobile ? (
        <CategoryButtonWrapper>
          <Chevron onClick={() => setShowTags(!showTags)} $isOpen={showTags} />
          <CategoryButton
            onClick={() => setShowTags(!showTags)}
            variant={TextVariant.ButtonSmall}
            $disabled={!showTags}
          >
            Kategorier
          </CategoryButton>
        </CategoryButtonWrapper>
      ) : null}
      <AnimatedFiltersContainer $isVisible={isVisible} $height={height} aria-hidden={!isVisible}>
        <FiltersWrapper ref={filtersRef}>
          <ChipsList>
            <Chip
              label={`Alle rabatter (${allBenefitsCount})`}
              onClick={() => {
                dispatch(actions.clearTags());
              }}
              variant={hasNoSelectedTags ? 'filled' : 'outlined'}
              color="primary"
              startIcon={hasNoSelectedTags ? Check : undefined}
              aria-label="Alle rabatter"
            />
            {isAuthenticated && favoriteBenefitsCount > 0 && (
              <Chip
                label={`Mine favoritter (${favoriteBenefitsCount})`}
                onClick={() => {
                  dispatch(actions.toggleFavoritesOnly());
                }}
                variant={favoritesOnly ? 'filled' : 'outlined'}
                color="primary"
                aria-label="Mine favoritter"
              />
            )}
            {displayedFilters.map((tag) => {
              const isTagSelected = tags.includes(tag.slug);
              return (
                <Chip
                  key={tag.id}
                  label={`${tag.title} (${tag.count})`}
                  onClick={() => {
                    dispatch(actions.selectTag({ tag }));
                  }}
                  variant={isTagSelected ? 'filled' : 'outlined'}
                  color="primary"
                  startIcon={isTagSelected ? Check : undefined}
                  aria-label={tag.title}
                />
              );
            })}
          </ChipsList>
        </FiltersWrapper>
      </AnimatedFiltersContainer>
    </GridCol>
  );
};
